import { useState, useEffect, useCallback } from 'react';
import { PaginationParams, PaginatedResponse } from '@models/index';

export interface UseServerPaginationProps<T> {
  fetchFunction: (params: PaginationParams) => Promise<PaginatedResponse<T>>;
  initialPageSize?: number;
  initialSearch?: string;
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
}

export interface UseServerPaginationReturn<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearch: (search: string) => void;
  setSortBy: (sortBy: string) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  refresh: () => void;
  /**
   * Aplica uma alteração pontual a itens já carregados, sem refazer a busca.
   * Para edição de uma célula, refetch devolveria o usuário ao topo da lista.
   */
  atualizarItem: (seletor: (item: T) => boolean, patch: Partial<T>) => void;
}

export function useServerPagination<T>({
  fetchFunction,
  initialPageSize = 10,
  initialSearch = '',
  initialSortBy = 'nome',
  initialSortOrder = 'asc'
}: UseServerPaginationProps<T>): UseServerPaginationReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearch] = useState(initialSearch);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: initialPageSize,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchFunction({
        page,
        pageSize,
        search,
        sortBy,
        sortOrder
      });

      setData(result.items);
      setPagination(result.pagination);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados');
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, page, pageSize, search, sortBy, sortOrder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page quando search ou pageSize mudar
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [search, pageSize]);

  return {
    data,
    loading,
    error,
    pagination,
    setPage,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1); // Reset para primeira página
    },
    setSearch: (searchTerm: string) => {
      setSearch(searchTerm);
      setPage(1); // Reset para primeira página
    },
    setSortBy,
    setSortOrder,
    refresh: loadData,
    atualizarItem: (seletor, patch) =>
      setData((atual) =>
        atual.map((item) => (seletor(item) ? { ...item, ...patch } : item))
      )
  };
}
